import { DownOutlined, FundOutlined } from '@ant-design/icons';
import { Button, Divider, Dropdown, Flex, Tag, Typography, theme } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';

import type { FinancialStatisticsComponentProps } from '@app/components/FinancialStatisticsComponent/FinancialStatisticsComponent.types';
import { divideAmount } from '@app/utils';

import { FinancialStatisticsDropdownItems } from './data/FinancialStatisticsDropdownItems';

import '@app/components/FinancialStatisticsComponent/styles/FinancialStatisticsComponent.desktop.scss';
import '@app/components/FinancialStatisticsComponent/styles/FinancialStatisticsComponent.mobile.scss';

const { Text } = Typography;
const { useToken } = theme;

export const FinancialStatisticsComponent: React.FC<
  FinancialStatisticsComponentProps
> = ({
  financialStatistics,
  dateString,
  isCalendarComponent,
  dropdownButtonDataTestId = '',
}) => {
  const { token } = useToken();

  const contentStyle: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
  };

  const menuStyle: React.CSSProperties = {
    boxShadow: 'none',
  };

  const dropdownItems = FinancialStatisticsDropdownItems({
    financialStatistics,
    isCalendarComponent,
  });

  const salesPercent = useMemo(() => {
    if (financialStatistics) {
      const formattedSalesPlanToday = divideAmount(
        financialStatistics.SalesPlanToday.toFixed(0)
      );
      const formattedPlanCompletionToday =
        financialStatistics.PlanCompletionToday.toFixed(0);

      return `${formattedPlanCompletionToday} % - ${formattedSalesPlanToday}`;
    } else {
      return 'План не установлен';
    }
  }, [financialStatistics]);

  return (
    <div className="financial-statistics-container">
      {isCalendarComponent ? (
        <>
          <FundOutlined className="red-color" />
          <Text data-testid="plan" className="red-color">
            {salesPercent}
          </Text>
        </>
      ) : (
        ''
      )}
      <Dropdown
        menu={{ items: dropdownItems }}
        trigger={['click']}
        popupRender={(menu) => (
          <div style={contentStyle}>
            {isCalendarComponent ? (
              <div
                className={
                  dateString
                    ? 'calendar-financial-statistics-header-container space-between'
                    : 'calendar-financial-statistics-header-container'
                }
              >
                {dateString ? (
                  <div data-testid="date">
                    {moment(dateString).locale('ru').format('DD MMMM')}
                  </div>
                ) : (
                  ''
                )}
                <Flex gap={10}>
                  <Tag
                    data-testid="clients"
                    className="client-tag tag"
                  >{`Клиентов: ${financialStatistics?.CountClients}`}</Tag>
                  <Tag
                    data-testid="clients-no-visited"
                    className="client-no-visited-tag tag"
                  >{`Отмен: ${financialStatistics?.CountClientNoVisited}`}</Tag>
                </Flex>
              </div>
            ) : (
              <div className="financial-statistics-header-container">
                <div data-testid="clients" className="dropdown-item">
                  <div data-testid="clients-title">Клиентов:</div>
                  <div data-testid="clients-value">
                    {financialStatistics?.CountClients}
                  </div>
                </div>
                <div className="dropdown-item">
                  <div data-testid="clients-came-title">Клиентов пришло:</div>
                  <div data-testid="clients-canceled-value">
                    {financialStatistics?.CountClientVisited}
                  </div>
                </div>
                <div className="dropdown-item">
                  <div data-testid="clients-canceled-title">
                    Клиентов отменено:
                  </div>
                  <div data-testid="clients-canceled-value">
                    {financialStatistics?.CountClientNoVisited}
                  </div>
                </div>
                <Divider className="divider" />
              </div>
            )}
            {React.cloneElement(menu as React.ReactElement, {
              style: menuStyle,
            })}
          </div>
        )}
      >
        <Button data-testid={dropdownButtonDataTestId}>
          {`${
            isCalendarComponent
              ? financialStatistics?.AmountTotal.toLocaleString() || 0
              : financialStatistics?.RecordsPaidAmount.toLocaleString() || 0
          }`}
          <DownOutlined />
        </Button>
      </Dropdown>
    </div>
  );
};
